import * as React from 'react';
import { Redirect } from 'react-router';
import * as _ from 'underscore';

import { Favorites } from '../../../ts/resources/Favorites';
import { Views } from '../../../ts/resources/Views';
import { ServiceLocator } from '../../ServiceLocator';
import { RouterError } from '../RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../SpacesSiteUrls';

// This isn't named RedirectToFavoritedViewProvider because we use this redirect for view links everywhere
// (see ViewDefinition's contentSpaceUrl) and in the favorites menu. We want different behaviors based on
// which redirect this is though.
export type RedirectViewType = 'favorite' | 'regular';

export interface RedirectToViewProviderProps {
  readonly viewId: string;
  readonly urls: SpacesSiteUrls;
  readonly redirectType: RedirectViewType;
}

export interface RedirectToViewProviderState {
  readonly viewPath: string;
  readonly hasError: boolean;
}

const InitialState: RedirectToViewProviderState = {
  viewPath: undefined,
  hasError: false
};

export class RedirectToViewProvider extends React.PureComponent<RedirectToViewProviderProps, RedirectToViewProviderState> {
  private unmounted: boolean = false;

  public constructor(props: RedirectToViewProviderProps) {
    super(props);
    this.state = InitialState;
  }

  public render(): JSX.Element {
    if (this.state.hasError) {
      return <RouterError errorType={this.props.redirectType === 'favorite' ? 'view-removed-favorite' : 'view'} urls={this.props.urls} />;
    } else if (!_.isUndefined(this.state.viewPath)) {
      return <Redirect to={this.props.urls.viewPath(this.state.viewPath)} />;
    }

    return null;
  }

  public async componentDidMount() {
    if (_.isUndefined(this.props.viewId)) {
      this.setToErrorStateAndRemoveFromFavorites();
      return;
    }

    try {
      const Views = ServiceLocator.get<Views>('Views');
      const view = await Views.getById(this.props.viewId);
      if (view) {
        this.updateState({ viewPath: view.path }) ;
      } else {
        this.setToErrorStateAndRemoveFromFavorites();
      }

    } catch (e) {
      this.setToErrorStateAndRemoveFromFavorites();
    }
  }

  private setToErrorStateAndRemoveFromFavorites() {
    if (this.props.redirectType === 'favorite') {
      const Favorites = ServiceLocator.get<Favorites>('Favorites');
      Favorites.removeViewById(this.props.viewId);
    }
    this.updateState({ hasError: true });
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  private updateState<K extends keyof RedirectToViewProviderState>(newState: Pick<RedirectToViewProviderState, K>): void {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }
}
